import {
  type ThemeOptions,
  type ComponentsProps,
  type ComponentsOverrides,
  type ComponentsVariants
} from '@mui/material/styles';

import { type Theme } from '../ThemeRegistry/theme.types';

import { CardVariants } from './Card.types';

const defaultProps: ComponentsProps['Card'] = {};

const styleOverrides: ComponentsOverrides<Theme>['Card'] = {
  root: ({ theme, ownerState }) => ({
    'containerType': 'inline-size',
    'height': '100%',

    '&  .MuiTypography-root': {
      whiteSpace: 'initial'
    }
  }),

  cardWrap: ({ ownerState }) => ({
    display: 'flex',
    flexDirection: 'column',
    boxShadow: 'unset',
    position: 'relative',
    gap: 'var(--grid-gap)',
    borderRadius: 'var(--grid-gap-half)',
    height: '100%',
    padding: 'var(--grid-gap)',

    ...(ownerState?.backgroundColor === 'lighterBlue'
      ? {
          backgroundColor: 'var(--mui-palette-schemes-blue-primary-lighter)'
        }
      : {
          backgroundColor: `var(--mui-palette-schemes-${ownerState?.backgroundColor}-primary-main)`
        })
  }),

  bodyWrap: {
    h6: {
      backgroundColor: 'var(--mui-palette-schemes-blue-primary-lighter)',
      padding: 'var(--grid-gap-quarter) var(--grid-gap-half) !important',
      borderRadius: 'var(--grid-gap-quarter)',
      textAlign: 'left',
      width: 'fit-content',
      marginTop: 0,
      marginBottom: 0
    }
  },

  media: ({ theme }) => ({
    'backgroundColor': 'inherit',
    'margin': 'auto',

    ':is(picture, svg)': {
      display: 'flex',
      height: '100%',
      width: '100%'
    },

    '& :is(video, img, svg)': {
      borderRadius: theme.spacing(4),
      aspectRatio: '5/4',
      [theme.containerBreakpoints.only('xs')]: {
        borderRadius: theme.spacing(3)
      }
    }
  }),

  contentWrap: ({ ownerState, theme }) => ({
    flex: 1,
    maxHeight: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--grid-gap)',

    ...((!!ownerState?.icon || !!ownerState?.icons) && {
      ':is(ol, ul)': {
        'padding': 'unset !important',

        'li': {
          marginLeft: '0 !important',

          p: {
            display: 'flex',
            textAlign: 'left',

            svg: {
              marginRight: theme.spacing(2)
            }
          }
        },

        'li::marker': {
          content: 'none'
        }
      }
    }),

    ...((!!CardVariants.stat || !!ownerState?.backgroundColor) && {
      '& *': {
        color: `var(--mui-palette-schemes-${ownerState?.backgroundColor}-primary-contrastText)`
      }
    })
  }),

  overline: ({ ownerState }) => ({
    ...(!!ownerState?.highlightOverline && {
      background: 'var(--mui-palette-schemes-blue-primary-lighter)',
      padding: 'var(--grid-gap-quarter) var(--grid-gap-half) !important',
      borderRadius: 'var(--grid-gap-quarter)',
      width: 'fit-content'
    })
  }),

  iconForListCard: ({ theme }) => ({
    [theme.containerBreakpoints.only('xs')]: {
      position: 'absolute',
      top: theme.spacing(5),
      padding: '0',
      left: theme.spacing(4)
    }
  }),

  disclaimerText: {
    margin: 0,
    textAlign: 'center'
  },

  actionsWrap: {
    padding: 0
  },

  link: {
    'position': 'absolute',
    'top': 0,
    'left': 0,
    'width': '100%',
    'height': '100%',
    'opacity': 0,

    '&:hover': {
      '.MuiCardActionArea-focusHighlight': {
        opacity: 0
      }
    }
  }
};

const createVariants = (theme: Theme): ComponentsVariants['Card'] => [
  {
    props: {
      variant: CardVariants.default
    },
    style: {
      '& *': {
        textAlign: 'center'
      },

      '[class*=cardMedia]': {
        '& :is(img, svg)': {
          borderRadius: '50%',
          aspectRatio: '1/1'
        }
      },

      '[class*=Card-contentWrap]': {
        alignItems: 'center',
        gap: 'var(--grid-gap)'
      },

      '[class*=cardWrap]': {
        padding: 'var(--grid-gap)',
        border: '2px solid'
      },

      '[class*=bodyWrap]': {
        textAlign: 'center'
      },

      '[class*=actionsWrap]': {
        alignSelf: 'center',
        textAlign: 'center'
      }
    }
  },

  {
    props: {
      variant: CardVariants.defaultContain
    },
    style: {
      '& *': {
        textAlign: 'center'
      },

      '[class*=cardMedia]': {
        '& :is(img, svg)': {
          borderRadius: '50%',
          aspectRatio: '1/1'
        }
      },

      '[class*=Card-contentWrap]': {
        alignItems: 'center',
        gap: 'var(--grid-gap)'
      },

      '[class*=cardWrap]': {
        padding: 'var(--grid-gap)',
        border: '2px solid'
      },

      '[class*=bodyWrap]': {
        textAlign: 'center'
      },

      '[class*=actionsWrap]': {
        alignSelf: 'center',
        textAlign: 'center'
      },

      'picture :is(video, img, svg)': {
        objectFit: 'contain'
      }
    }
  },

  {
    props: {
      variant: CardVariants.person
    },
    style: {
      '[class*=cardMedia]': {
        '& :is(img, svg)': {
          objectFit: 'cover',
          borderRadius: '50%',
          aspectRatio: '1/1'
        }
      },

      '[class*=contentWrap]': {
        alignItems: 'center',
        padding: 'var(--grid-gap)'
      },

      '[class*=cardWrap]': {
        padding: 0,
        border: '1px solid',
        gap: 0
      },

      '[class*=bodyWrap]': {
        '& *': {
          textAlign: 'center'
        }
      },

      '[class*=subtitle]': {
        textAlign: 'center'
      },

      '[class*=actionsWrap]': {
        alignSelf: 'center'
      }
    }
  },

  {
    props: {
      variant: CardVariants.quote
    },
    style: {
      '[class*=Card-contentWrap]': {
        alignItems: 'center'
      },

      '[class*=cardWrap]': {
        padding: 'var(--grid-gap)'
      },

      '[class*=bodyWrap]': {
        textAlign: 'center'
      },

      '[class*=actionsWrap]': {
        alignSelf: 'center'
      },

      '[class*=Card-body]': {
        '& > *:last-child': {
          marginTop: 'var(--grid-gap-lg)'
        }
      }
    }
  },

  {
    props: {
      variant: CardVariants.blog
    },
    style: {
      '[class*=Card-contentWrap]': {
        alignItems: 'start',
        padding: 0,
        gap: 'var(--grid-gap-half)',

        [theme.breakpoints.only('xs')]: {
          gap: 'var(--grid-gap)'
        }
      },

      '[class*=cardMedia]': {
        display: 'none' // TODO: Remove this once they have more images
        // '& :is(img, svg, picture > img)': {
        //   marginBottom: theme.spacing(2),
        //   objectFit: 'cover',
        //   aspectRatio: '16/9',

        //   [theme.containerBreakpoints.only('xs')]: {
        //     marginBottom: theme.spacing(1)
        //   }
        // }
      },

      '[class*=actionsWrap]': {
        ':is(span)': {
          paddingLeft: theme.spacing(0.25),
          marginRight: 0,
          marginLeft: theme.spacing(-1.25)
        }
      },

      '[class*=socialLinks]': {
        display: 'flex',
        gap: 'var(--grid-gap-half)',

        [theme.breakpoints.only('xs')]: {
          gap: 'var(--grid-gap)'
        },

        a: {
          padding: 0
        }
      },

      '[class*=cardWrap]': {
        padding: 0,
        gap: 'var(--grid-gap)',
        borderRadius: 0
      },

      '[class*=subtitle]': {
        ...theme.typography.body2
      }
    }
  },

  {
    props: {
      variant: CardVariants.titleOnly
    },
    style: {
      '[class*=Card-contentWrap]': {
        gap: 0,
        paddingTop: 0,
        paddingBottom: 0
      },

      '[class*=cardMedia]': {
        display: 'none'
      },

      '[class*=actionsWrap]': {
        display: 'none'
      },

      '[class*=socialLinks]': {
        display: 'none'
      },

      '[class*=cardWrap]': {
        padding: 0,
        gap: 'var(--grid-gap)',
        borderRadius: 0
      },

      '[class*=subtitle]': {
        display: 'none'
      },

      '[class*=bodyWrap]': {
        display: 'none'
      },

      '[class*=overline]': {
        display: 'none'
      }
    }
  },

  {
    props: {
      variant: CardVariants.titleAndSummaryOnly
    },
    style: {
      '[class*=Card-contentWrap]': {
        gap: 0,
        paddingTop: 0,
        paddingBottom: 0
      },

      '[class*=cardMedia]': {
        display: 'none'
      },

      '[class*=actionsWrap]': {
        display: 'none'
      },

      '[class*=socialLinks]': {
        display: 'none'
      },

      '[class*=bodyWrap]': {
        paddingTop: 'var(--grid-gap-half)'
      },

      '[class*=cardWrap]': {
        padding: 0,
        gap: 'var(--grid-gap)',
        borderRadius: 0
      },

      '[class*=subtitle]': {
        display: 'none'
      },

      '[class*=overline]': {
        display: 'none'
      }
    }
  },

  {
    props: {
      variant: CardVariants.blogWithImage
    },
    style: {
      '[class*=Card-contentWrap]': {
        alignItems: 'start',
        padding: 0,
        gap: 'var(--grid-gap-half)',

        [theme.breakpoints.only('xs')]: {
          gap: 'var(--grid-gap)'
        }
      },

      '[class*=cardMedia]': {
        '& :is(img, svg)': {
          objectFit: 'cover',
          borderRadius: '50%',
          aspectRatio: '1/1'
        }

        // '& :is(img, svg, picture > img)': {
        //   marginBottom: theme.spacing(2),
        //   objectFit: 'cover',
        //   aspectRatio: '16/9',

        //   [theme.containerBreakpoints.only('xs')]: {
        //     marginBottom: theme.spacing(1)
        //   }
        // }
      },

      '[class*=actionsWrap]': {
        ':is(span)': {
          paddingLeft: theme.spacing(0.25),
          marginRight: 0,
          marginLeft: theme.spacing(-1.25)
        }
      },

      '[class*=socialLinks]': {
        display: 'flex',
        gap: 'var(--grid-gap-half)',

        [theme.breakpoints.only('xs')]: {
          gap: 'var(--grid-gap)'
        },

        a: {
          padding: 0
        }
      },

      '[class*=cardWrap]': {
        padding: 0,
        gap: 'var(--grid-gap)',
        borderRadius: 0
      },

      '[class*=subtitle]': {
        ...theme.typography.body2
      }
    }
  },

  {
    props: {
      variant: CardVariants.media
    },
    style: {
      '[class*=Card-contentWrap]': {
        alignItems: 'start',
        gap: 'var(--grid-gap-md)'
      },

      '[class*=cardWrap]': {
        borderRadius: 0
      }
    }
  },

  {
    props: {
      variant: CardVariants.icon
    },

    style: {
      '[class*=Card-title]': {
        ...theme.typography.h3
      },

      '[class*=cardWrap]': {
        border: '2px solid',
        flexDirection: 'column-reverse !important',

        padding: 'var(--grid-gap)',

        [theme.containerBreakpoints.only('xs')]: {
          flexDirection: 'column !important',
          gap: 'var(--grid-gap)'
        }
      },

      '[class*=actionsWrap]': {
        justifyContent: 'space-between',
        flexDirection: 'row-reverse',

        [theme.containerBreakpoints.only('xs')]: {
          display: 'contents'
        }
      }
    }
  },

  {
    props: {
      variant: CardVariants.pricing
    },
    style: {
      '[class*=Card-contentWrap]': {
        alignItems: 'center',
        gap: 'var(--grid-gap-xs)'
      },

      '[class*=cardWrap]': {
        padding: 'var(--grid-gap)'
      },

      '[class*=bodyWrap]': {
        'textAlign': 'center',
        'marginTop': 'var(--grid-gap)',

        'ul, ol': {
          'marginTop': 'var(--grid-gap)',

          ':is(svg)': {
            width: theme.spacing(2)
          }
        }
      },

      '[class*=actionsWrap]': {
        alignSelf: 'center',
        width: '100%',
        a: {
          width: '100%'
        }
      },

      '[class*=disclaimerText]': {
        textAlign: 'left'
      }
    }
  },

  {
    props: {
      variant: CardVariants.logo
    },
    style: {
      '[class*=cardMedia]': {
        'padding': 'var(--grid-gap-lg)',
        '& :is(img, svg)': {
          maxHeight: '25cqi',
          objectFit: 'contain',
          borderRadius: 'unset',
          aspectRatio: 'unset'
        }
      },

      '.MuiCardActionArea-focusHighlight': {
        background: 'transparent'
      },

      '[class*=cardWrap]': {
        borderRadius: 'var(--grid-gap)',
        background: 'transparent'
      },

      '[class*=contentWrap]': {
        display: 'none'
        // textAlign: 'center',
      }
    }
  },

  {
    props: {
      variant: CardVariants.iconList
    },
    style: {
      '[class*=cardWrap]': {
        borderRadius: 0
      },

      '[class*=bodyWrap]': {
        textAlign: 'left',

        h6: {
          marginTop: 0,
          borderRadius: theme.spacing(2),
          padding: theme.spacing(2, 3)
        },

        ul: {
          marginBlockStart: 'unset'
        },

        li: {
          'marginTop': 'var(--grid-gap)',
          'marginBottom': 0,

          [theme.containerBreakpoints.only('xs')]: {
            marginTop: 'var(--grid-gap-lg)'
          },
          ':is(svg)': {
            width: theme.spacing(2)
          }
        },

        p: {
          ...theme.typography.h6
        }
      }
    }
  },

  {
    props: {
      variant: CardVariants.autocomplete
    },
    style: {
      '[class*=cardWrap]': {
        // borderBottom: '1px solid gray',
        'borderRadius': '0',
        'padding': '0', //'var(--grid-gap-half) var(--grid-gap-quarter)',

        '&:hover': {
          backgroundColor: 'var(--mui-palette-primary-main)'
        },

        '[class*=contentWrap]': {
          'gap': `var(--grid-gap-quarter)`,
          'padding': `var(--grid-gap-half)`,

          '[class*=Card-overline]': {
            ...theme.typography.overline,
            padding: `${theme.spacing(0.25, 0.5)} !important`
          }
        },

        '[class*=bodyWrap]': {
          display: 'none'
        }
      },

      '[class*=cardMedia]': {
        display: 'none'
      },

      '[class*=actionsWrap]': {
        display: 'none'
      }
    }
  },

  {
    props: {
      variant: CardVariants.stat
    },
    style: {
      '& *': {
        textAlign: 'center'
      },
      '[class*=actionsWrap]': {
        marginLeft: 'auto',
        marginRight: 'auto'
      },

      '[class*=Card-contentWrap]': {
        alignItems: 'center',
        gap: 'var(--grid-gap)'
      },

      '[class*=cardWrap]': {
        padding: 'var(--grid-gap)'
        // border: '2px solid'
      }
    }
  },

  {
    props: {
      variant: CardVariants.jobposting
    },

    style: {
      '[class*=Card-title]': {
        ...theme.typography.subtitle2
      },

      '[class*=Card-subtitle]': {
        ...theme.typography.subtitle1,

        [theme.containerBreakpoints.only('xs')]: {
          ...theme.typography.body1,
          fontWeight: 500
        }
      },

      '[class*=cardWrap]': {
        'border': '2px solid',
        'flexDirection': 'column-reverse !important',
        'borderRadius': theme.spacing(4),
        'gap': 'var(--grid-gap-half)',
        'boxShadow': 'none',
        'padding': 'var(--grid-gap)',

        [theme.containerBreakpoints.only('xs')]: {
          border: '1px solid',
          flexDirection: 'column !important',
          padding: 'var(--grid-gap-double)',
          gap: 'var(--grid-gap-double)'
        },

        '& *': {
          order: '2'
        }
      },

      '[class*=actionsWrap]': {
        justifyContent: 'space-between',
        flexDirection: 'row-reverse',
        a: {
          ...theme.typography.body1
        },

        [theme.containerBreakpoints.only('xs')]: {
          'display': 'contents',

          '[class*="iconForListCard"]': {
            display: 'none',
            order: '1',
            position: 'static'
          },

          '& > a': { order: '5', width: '100%' }
        }
      },

      '[class*=contentWrap]': {
        gap: theme.spacing(3),

        [theme.containerBreakpoints.only('xs')]: {
          display: 'contents',
          gap: theme.spacing(2)
        }
      }
    }
  }
];

export const cardTheme = (theme: Theme): ThemeOptions => ({
  components: {
    Card: {
      defaultProps,
      styleOverrides,
      variants: createVariants(theme)
    }
  }
});

export default cardTheme;
